/**
 * IdOcrModal — Runs OCR on the candidate's uploaded pièce d'identité
 * (Claude Sonnet 4.5 vision) and lets an admin one-click apply the
 * extracted values to the candidate profile.
 *
 * Flow (CEO 2026-07-30 iter63):
 *   1. Modal opens → POST /api/candidates/{id}/id-ocr
 *   2. Backend loads the CNI/passport from Emergent storage, runs OCR,
 *      returns { extracted, current, divergence, run_id }.
 *   3. UI shows a side-by-side diff (fiche vs OCR). Admin ticks the
 *      fields to apply (all divergent fields ticked by default).
 *   4. Admin clicks "Appliquer" → POST /api/candidates/{id}/id-ocr/apply
 *      with the ticked payload. Fiche is updated, form is synced
 *      (iter60), audit trail is written (iter59).
 *   5. Modal closes and calls onSaved() to refresh the profile.
 *
 * Idempotent design — the admin can re-run OCR any time; each call
 * inserts a fresh audit row so we can trace the exact CNI version
 * used at the moment of correction.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// Field labels used across the diff table.
// Two families:
//   1. CANDIDATE-scoped — flow into candidates.* AND onboarding_forms.data.*
//      via iter60 sync; the SYNTEC contract reads these first (iter62).
//   2. FORM-scoped — flow into onboarding_forms.data.* only. These are
//      administrative fields that end up in Article 1 of the contract
//      through the form snapshot.
const FIELDS = [
    { key: "civilite", label: "Civilité", scope: "candidate" },
    { key: "first_name", label: "Prénom(s)", scope: "candidate" },
    { key: "last_name", label: "Nom", scope: "candidate" },
    { key: "birth_date", label: "Date de naissance", scope: "candidate" },
    { key: "birth_city", label: "Lieu de naissance", scope: "form" },
    { key: "birth_country", label: "Pays de naissance", scope: "form" },
    { key: "nationality", label: "Nationalité", scope: "form" },
];

// Purely-informational fields shown at the bottom (KYC / audit only —
// no direct write path, kept for reference).
const INFO_FIELDS = [
    { key: "id_number", label: "N° de pièce" },
    { key: "expiry_date", label: "Expiration" },
];

export default function IdOcrModal({ candidateId, onClose, onSaved }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    // Selection state: which extracted fields the admin wants to apply
    const [selection, setSelection] = useState({});
    const [applying, setApplying] = useState(false);
    // In-flight guard — React.StrictMode double-mounts effects in dev,
    // which would fire the (expensive) Claude Sonnet OCR twice. In
    // production StrictMode is off so this is a no-op there. It also
    // guards against the user quickly closing + reopening the modal.
    const inflightRef = useRef(false);

    const runOcr = useCallback(async () => {
        if (inflightRef.current) return;
        inflightRef.current = true;
        setLoading(true);
        setError(null);
        try {
            // Try the cache first — if the candidate uploaded their CNI
            // recently the auto-OCR (iter64) has already run in the
            // background, so we can show the result instantly with zero
            // extra LLM cost. Falls back to the full run endpoint when
            // no cache exists (204).
            let data = null;
            try {
                const { data: cached, status } = await api.get(
                    `/candidates/${candidateId}/id-ocr/cached`,
                    { validateStatus: (s) => s === 200 || s === 204 }
                );
                if (status === 200 && cached) data = cached;
            } catch (_e) {
                // Silent — 204/404 both mean "no cache" and are fine.
            }
            if (!data) {
                const { data: fresh } = await api.post(`/candidates/${candidateId}/id-ocr`);
                data = fresh;
            }
            setResult(data);
            // Auto-tick every divergent field (by default the admin wants
            // to apply the OCR fix — they can untick if they disagree).
            const initial = {};
            for (const f of FIELDS) {
                if (data.divergence?.[f.key]) initial[f.key] = true;
            }
            setSelection(initial);
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
            setError(detail);
        } finally {
            setLoading(false);
            inflightRef.current = false;
        }
    }, [candidateId]);

    // Force a fresh LLM run (bypassing the cache) — used when the admin
    // wants to re-analyse the document (e.g. the candidate re-uploaded
    // a clearer image).
    const forceRerun = useCallback(async () => {
        if (inflightRef.current) return;
        inflightRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.post(`/candidates/${candidateId}/id-ocr`);
            setResult(data);
            const initial = {};
            for (const f of FIELDS) {
                if (data.divergence?.[f.key]) initial[f.key] = true;
            }
            setSelection(initial);
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
            setError(detail);
        } finally {
            setLoading(false);
            inflightRef.current = false;
        }
    }, [candidateId]);

    useEffect(() => { runOcr(); }, [runOcr]);

    const toggle = (key) => setSelection((s) => ({ ...s, [key]: !s[key] }));

    const anyTicked = useMemo(() => Object.values(selection).some(Boolean), [selection]);

    const handleApply = async () => {
        if (!anyTicked) return;
        setApplying(true);
        try {
            const payload = { run_id: result?.run_id };
            for (const f of FIELDS) {
                if (selection[f.key]) payload[f.key] = result.extracted?.[f.key] ?? null;
            }
            const { data } = await api.post(`/candidates/${candidateId}/id-ocr/apply`, payload);
            toast.success(
                data.updated > 0
                    ? `Fiche mise à jour (${data.updated} champ${data.updated > 1 ? "s" : ""})`
                    : "Aucune modification appliquée"
            );
            onSaved?.();
            onClose?.();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
            toast.error(`Impossible d'appliquer : ${detail}`);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            data-testid="id-ocr-modal"
            onClick={onClose}
        >
            <div
                className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                            <ScanLine className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-shariti-black">
                                Lecture automatique de la pièce d&apos;identité
                            </h2>
                            <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
                                Claude Sonnet 4.5 lit la CNI / titre de séjour / passeport et propose les valeurs à appliquer.
                                Vous gardez la main sur ce qui est écrit sur la fiche.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="id-ocr-close"
                        className="h-8 w-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="id-ocr-loading">
                            <Loader2 className="h-8 w-8 text-shariti-orange animate-spin mb-3" />
                            <div className="text-sm font-bold text-shariti-black">Analyse en cours…</div>
                            <div className="text-[12px] text-slate-500 mt-1">Cela prend généralement 5 à 15 secondes.</div>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="id-ocr-error">
                            <AlertCircle className="h-8 w-8 text-shariti-red mb-3" />
                            <div className="text-sm font-bold text-shariti-black mb-1">Impossible de lire la pièce</div>
                            <div className="text-[12px] text-slate-600 max-w-md">{error}</div>
                            <button
                                onClick={runOcr}
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white px-4 py-2 text-[12px] font-bold hover:opacity-90"
                            >
                                Réessayer
                            </button>
                        </div>
                    )}

                    {!loading && !error && result && (
                        <div className="space-y-5" data-testid="id-ocr-result">
                            {/* Doc type banner + cache metadata */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-700 uppercase tracking-widest">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                    {docTypeLabel(result.document_type)}
                                </div>
                                {result.cached && (
                                    <div
                                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 text-[10px] font-black uppercase tracking-widest"
                                        data-testid="id-ocr-cache-badge"
                                        title="Extraction déjà réalisée en tâche de fond au moment de l'upload"
                                    >
                                        <CheckCircle2 className="h-3 w-3" /> Extraction pré-calculée
                                    </div>
                                )}
                                {result.notes && (
                                    <span className="text-[11px] text-slate-500 font-medium">· {result.notes}</span>
                                )}
                            </div>

                            {/* Diff table */}
                            <div className="rounded-2xl border border-slate-200 overflow-hidden">
                                <div className="grid grid-cols-[24px_1fr_1fr_120px] gap-x-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    <div></div>
                                    <div>Champ · Fiche actuelle</div>
                                    <div>Extrait de la pièce</div>
                                    <div className="text-right">Confiance</div>
                                </div>
                                {FIELDS.map((f) => {
                                    const cur = result.current?.[f.key] ?? null;
                                    const ocr = result.extracted?.[f.key] ?? null;
                                    const conf = result.extracted?.confidence?.[f.key];
                                    const diff = !!result.divergence?.[f.key];
                                    const ticked = !!selection[f.key];
                                    return (
                                        <div
                                            key={f.key}
                                            className={`grid grid-cols-[24px_1fr_1fr_120px] gap-x-3 items-center px-4 py-3 border-b border-slate-100 last:border-b-0 ${diff ? "bg-amber-50/40" : "bg-white"}`}
                                            data-testid={`id-ocr-row-${f.key}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={ticked}
                                                disabled={!ocr}
                                                onChange={() => toggle(f.key)}
                                                data-testid={`id-ocr-toggle-${f.key}`}
                                                className="h-4 w-4 accent-shariti-orange disabled:opacity-30"
                                            />
                                            <div>
                                                <div className="flex items-center gap-1.5 mb-0.5">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{f.label}</span>
                                                    <ScopePill scope={f.scope} />
                                                </div>
                                                <div className={`text-[13px] font-bold ${cur ? "text-slate-800" : "text-slate-400 italic"}`}>
                                                    {cur || "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <div className={`text-[13px] font-bold ${ocr ? (diff ? "text-emerald-700" : "text-slate-800") : "text-slate-400 italic"}`}>
                                                    {ocr || "illisible"}
                                                </div>
                                                {diff && (
                                                    <div className="text-[10px] text-amber-700 font-semibold mt-0.5">
                                                        ≠ fiche
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <ConfidencePill value={conf} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Info fields (read-only, for admin awareness) */}
                            {INFO_FIELDS.some((f) => result.extracted?.[f.key]) && (
                                <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                                        Autres informations lues (référence · KYC)
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {INFO_FIELDS.map((f) => {
                                            const v = result.extracted?.[f.key];
                                            if (!v) return null;
                                            return (
                                                <div key={f.key} className="text-[11px]">
                                                    <div className="font-semibold text-slate-500 uppercase tracking-widest text-[9px] mb-0.5">{f.label}</div>
                                                    <div className="font-bold text-slate-800">{v}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!loading && !error && result && (
                    <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-[11px] text-slate-500">
                            {Object.keys(result.divergence || {}).length === 0
                                ? "Aucune divergence détectée — la fiche est déjà alignée avec la pièce."
                                : `${Object.keys(result.divergence).length} divergence${Object.keys(result.divergence).length > 1 ? "s" : ""} détectée${Object.keys(result.divergence).length > 1 ? "s" : ""}.`}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={onClose}
                                className="rounded-full border border-slate-200 px-4 py-2 text-[12px] font-bold text-slate-600 hover:bg-slate-50"
                            >
                                Annuler
                            </button>
                            {result.cached && (
                                <button
                                    onClick={forceRerun}
                                    data-testid="id-ocr-rerun-btn"
                                    className="rounded-full border border-slate-200 px-4 py-2 text-[12px] font-bold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5"
                                    title="Force une nouvelle analyse par Claude Sonnet (utile si le candidat a re-uploadé une image plus nette)"
                                >
                                    <ScanLine className="h-3.5 w-3.5" /> Re-analyser
                                </button>
                            )}
                            <button
                                onClick={handleApply}
                                disabled={!anyTicked || applying}
                                data-testid="id-ocr-apply-btn"
                                className="rounded-full bg-shariti-orange text-white px-5 py-2 text-[12px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                            >
                                {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                Appliquer à la fiche
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function docTypeLabel(t) {
    switch (t) {
        case "cni_fr": return "Carte nationale d'identité";
        case "titre_sejour": return "Titre de séjour";
        case "passport": return "Passeport";
        default: return "Pièce d'identité";
    }
}

function ConfidencePill({ value }) {
    if (value == null) return <span className="text-slate-300 text-[11px]">—</span>;
    let tone = "bg-slate-100 text-slate-600";
    if (value >= 90) tone = "bg-emerald-100 text-emerald-700";
    else if (value >= 70) tone = "bg-amber-100 text-amber-700";
    else tone = "bg-red-100 text-red-700";
    return (
        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-black ${tone}`}>
            {value}%
        </span>
    );
}

/**
 * ScopePill — small tag showing which collection a field will be
 * written to when applied. Helps the admin understand that
 * "Nationalité" doesn't touch the fiche contact card but does end up
 * in the SYNTEC contract's Article 1.
 */
function ScopePill({ scope }) {
    if (scope === "form") {
        return (
            <span
                className="inline-block rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-[1px] text-[8px] font-black uppercase tracking-widest"
                title="Écrit dans le dossier d'embauche (utilisé par le contrat SYNTEC)"
            >
                dossier
            </span>
        );
    }
    return (
        <span
            className="inline-block rounded-full bg-orange-50 border border-orange-200 text-orange-700 px-1.5 py-[1px] text-[8px] font-black uppercase tracking-widest"
            title="Écrit dans la fiche candidat (source de vérité identité)"
        >
            fiche
        </span>
    );
}
